"use client";

import React, { useState, useEffect } from "react";
import { Instagram, Sparkles, ArrowRight, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";

/**
 * Footer.tsx (v4 — SLIM & HONEST)
 * ------------------------------------------------------
 * Nur Links zu Seiten, die TATSÄCHLICH existieren.
 * Slugs entsprechen 1:1 den WordPress-Slugs aus dem Editor.
 *
 * BEWUSST ENTFERNT (kommen zurück, sobald wir die Seiten gebaut haben):
 *  - "Website nach Branche" / "Webshop nach Branche" Mega-Sektion
 *  - "Website nach Kanton" / "Webshop nach Kanton" Mega-Sektion
 *  - Managed Hosting / Domain / SSL / SEO-Tools / Design-Vorlagen
 *  - Landing Page erstellen / Gesundheit & Wellness / Beratung & Recht
 *  - Blog / Hilfe / Tutorials / API-Dokumentation / Changelog
 *
 * ENTHALTEN (alles existiert in WordPress):
 *  - Startseite (/), /webshop-erstellen, /losung-fur-agenturen-freelancer,
 *  - /vermittlerprogramm, /pagemaker24-in-allen-kantonen,
 *  - /pagemaker24-fur-alle-branchen, /website-fur-coaches-berater,
 *  - /website-fur-gastronomie, /website-fur-handwerker,
 *  - /website-fur-immobilien
 */

const SIGNUP_URL = "https://dashboard.pagemaker24.ch/sign-up";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-slate-700/40 animate-pulse rounded-sm" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    // Fallback nur wenn WordPress-Menü leer ist — User kann diese Seiten später anlegen.
    return <ul className="space-y-3">
        <li data-index={0}><Link to="#" className="text-sm text-slate-400 hover:text-white transition-colors">Über uns</Link></li>
        <li data-index={1}><Link to="#" className="text-sm text-slate-400 hover:text-white transition-colors">Kontakt</Link></li>
        <li data-index={2}><Link to="#" className="text-sm text-slate-400 hover:text-white transition-colors">Datenschutz</Link></li>
        <li data-index={3}><Link to="#" className="text-sm text-slate-400 hover:text-white transition-colors">AGB</Link></li>
        <li data-index={4}><Link to="#" className="text-sm text-slate-400 hover:text-white transition-colors">Impressum</Link></li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-slate-400 hover:text-white transition-colors">{item.label}</Link>
        </li>)}
    </ul>;
}

/**
 * Floating Badge — bottom-right, sticky.
 * Variante B: "Live in unter 1 Stunde." + Sub + Button → /sign-up.
 */
function BuiltWithBadge() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);
  if (dismissed) return null;
  return <>
      <style>{`
        @keyframes bw-fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .bw-fade-in { animation: bw-fade-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes bw-pulse { 0%,100% { opacity: 1; } 50% { opacity: .5; } }
        .bw-pulse { animation: bw-pulse 1.6s ease-in-out infinite; }
      `}</style>
      <div className={`fixed bottom-4 right-4 z-40 ${visible ? "bw-fade-in" : "opacity-0 pointer-events-none"}`} style={{
      filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.25))"
    }}>
        <div className="relative bg-[#14151f] border border-white/10 rounded-2xl p-3 pr-3.5 max-w-[280px]">
          <div className="flex items-start gap-3">
            <span className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(16,88%,54%)]">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-400 bw-pulse" />
            </span>
            <div className="min-w-0 pr-3">
              <p className="text-white font-bold text-sm leading-snug">
                Live in unter 1 Stunde.
              </p>
              <p className="text-white/70 text-xs leading-snug mt-0.5">
                Diese Seite wurde mit PAGEMAKER24 gebaut.
              </p>
            </div>
          </div>
          <a href={SIGNUP_URL} className="mt-3 flex items-center justify-center gap-1.5 w-full rounded-lg bg-[hsl(16,88%,54%)] hover:bg-[hsl(16,88%,48%)] text-white text-xs font-semibold py-2 transition-all hover:scale-[1.02]">
            Gratis starten
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </a>
          <button onClick={() => setDismissed(true)} aria-label="Schliessen" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white/90 hover:bg-white text-gray-700 flex items-center justify-center shadow-md transition-colors">
            <X className="w-3 h-3" strokeWidth={3} />
          </button>
        </div>
      </div>
    </>;
}
export default function Footer() {
  const produkt = [{
    label: "KI Website-Builder",
    path: "/"
  }, {
    label: "KI Webshop-Builder",
    path: "/webshop-erstellen"
  }, {
    label: "Preise",
    path: "/#preise"
  }, {
    label: "Vermittlerprogramm",
    path: "/vermittlerprogramm"
  }];
  const loesungen = [{
    label: "Website für Gastronomie",
    path: "/website-fur-gastronomie"
  }, {
    label: "Website für Handwerker",
    path: "/website-fur-handwerker"
  }, {
    label: "Website für Coaches & Berater",
    path: "/website-fur-coaches-berater"
  }, {
    label: "Website für Immobilien",
    path: "/website-fur-immobilien"
  }, {
    label: "Lösung für Agenturen & Freelancer",
    path: "/losung-fur-agenturen-freelancer"
  }, {
    label: "Alle Branchen ansehen",
    path: "/pagemaker24-fur-alle-branchen"
  }, {
    label: "Alle Kantone ansehen",
    path: "/pagemaker24-in-allen-kantonen"
  }];
  return <>
      <BuiltWithBadge />

      <section data-section-id="186913" id="pagemaker24-footer" className="bg-[#14151f]">
        <div className="bg-[#1a1c23]">
          <Separator className="bg-slate-700/50" />

          <div className="mx-auto max-w-7xl px-6 py-16 md:px-8 lg:py-20">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-5 lg:gap-8">
              {/* Brand */}
              <div className="flex flex-col items-start space-y-6 lg:col-span-2">
                <Link to="/" className="inline-block">
                  <img src="https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Pagemaker_Logo_v2.png" alt="PAGEMAKER24" className="h-7 w-auto object-contain" />
                </Link>
                <p className="text-sm leading-relaxed text-slate-400 max-w-sm">
                  Die führende Plattform für Schweizer und deutsche KMU, um
                  professionelle Webseiten mit KI-Präzision und höchster
                  Sicherheit zu erstellen.
                </p>

                {/* ── Brand-Anker (Variante B) ── */}
                <a href={SIGNUP_URL} className="group block w-full max-w-sm rounded-xl bg-[hsl(16,88%,54%)]/10 border border-[hsl(16,88%,54%)]/30 hover:border-[hsl(16,88%,54%)]/60 hover:bg-[hsl(16,88%,54%)]/15 p-4 transition-all">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(16,88%,54%)] text-white">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm leading-snug text-white/90">
                        Diese Website läuft auf unserer eigenen Technologie.
                        Nutzen auch Sie PAGEMAKER24 — gehen Sie online und
                        gewinnen Sie Kunden.
                      </p>
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[hsl(16,88%,54%)] group-hover:gap-2 transition-all">
                        Gratis starten
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </span>
                    </div>
                  </div>
                </a>

                <div className="flex items-center space-x-4">
                  <a href="https://www.instagram.com/pagemaker24.official/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="Instagram">
                    <Instagram className="size-5" strokeWidth={1.5} />
                  </a>
                </div>
              </div>

              {/* Produkt */}
              <div>
                <h3 className="mb-6 text-sm font-bold tracking-widest text-white uppercase">
                  Produkt
                </h3>
                <ul className="space-y-3">
                  {produkt.map((item, i) => <li key={item.label} data-index={i}>
                      <Link to={item.path} className="text-sm text-slate-400 hover:text-white transition-colors">
                        {item.label}
                      </Link>
                    </li>)}
                </ul>
              </div>

              {/* Lösungen */}
              <div>
                <h3 className="mb-6 text-sm font-bold tracking-widest text-white uppercase">
                  Lösungen
                </h3>
                <ul className="space-y-3">
                  {loesungen.map((item, i) => <li key={item.label} data-index={i}>
                      <Link to={item.path} className="text-sm text-slate-400 hover:text-white transition-colors">
                        {item.label}
                      </Link>
                    </li>)}
                </ul>
              </div>

              {/* Unternehmen (WordPress-Menü) */}
              <div>
                <h3 className="mb-6 text-sm font-bold tracking-widest text-white uppercase">
                  Unternehmen
                </h3>
                <MenuProvider menu_id="footer_menu">
                  <FooterMenuConsumer />
                </MenuProvider>
              </div>
            </div>

            {/* ── Mission-Block (Weg 2) — über der Bottom-Bar ── */}
            <div className="mt-16 border-t border-slate-700/50 pt-10">
              <div className="mx-auto max-w-3xl text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 mb-4">
                  <span className="text-base leading-none"></span>
                  <span className="text-xs font-bold tracking-widest text-white uppercase">
                    Made in Switzerland
                  </span>
                </div>
                <p className="text-sm md:text-base leading-relaxed text-slate-300">
                  PAGEMAKER24 wurde von passionierten Schweizer Digitalexperten
                  entwickelt. Unser Ziel: jeder Schweizer Firma den Zugang zu
                  einer professionellen Website oder einem Onlineshop
                  ermöglichen.
                </p>
                <a href={SIGNUP_URL} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[hsl(16,88%,54%)] hover:bg-[hsl(16,88%,48%)] text-white text-sm font-semibold px-5 py-2.5 transition-all hover:scale-[1.03]">
                  Starten auch Sie
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </a>
              </div>
            </div>

            {/* ── Bottom-Bar (clean: nur Copyright + Standort) ── */}
            <div className="mt-12 border-t border-slate-700/50 pt-8 flex flex-col items-center justify-between gap-4 sm:flex-row">
              <p className="text-xs text-slate-500 text-center sm:text-left">
                © 2026 PAGEMAKER24. Alle Rechte vorbehalten.
              </p>
              <p className="text-xs text-slate-500">St. Gallen · Zürich — Schweiz</p>
            </div>
          </div>
        </div>
      </section>
    </>;
}